import { createContext, useContext, type ReactNode } from "react";

/**
 * Microsoft Clarity (loaded by `src/lib/monitoring/consent-tools.ts` once a visitor
 * allows analytics) records the text a page renders
 * and uploads it. Decrypted Life Design and coaching text, recovery codes and
 * the PIN must never leave the browser readable, so they render inside a
 * `RecordingMask`.
 *
 * Clarity masks every element that has the `data-clarity-mask` attribute,
 * and everything inside it, whatever masking mode the Clarity project uses
 * (https://learn.microsoft.com/en-us/clarity/setup-and-installation/clarity-masking).
 * Clarity checks only that the attribute is there; "True" is the documented value.
 *
 * Dialogs, drawers, menus and tooltips render in a portal at the end of
 * `<body>`, outside the masked element. Those `ui/` primitives call
 * `useRecordingMask()` to put the attribute on their own content when they
 * open from inside a mask.
 */

const MASK = { "data-clarity-mask": "True" } as const;
const NO_MASK = {} as const;

type RecordingMaskProps = typeof MASK | typeof NO_MASK;

const RecordingMaskContext = createContext(false);

export function RecordingMask({
  children,
}: {
  children: ReactNode;
}): ReactNode {
  return (
    <RecordingMaskContext.Provider value={true}>
      {/* `contents` gives the wrapper no box, so the page lays out as before. */}
      <div {...MASK} className="contents">
        {children}
      </div>
    </RecordingMaskContext.Provider>
  );
}

/** Attributes for portalled content: the mask inside a `RecordingMask`, none elsewhere. */
export function useRecordingMask(): RecordingMaskProps {
  return useContext(RecordingMaskContext) ? MASK : NO_MASK;
}
